"use client";

import { ErrorCard } from "@/components/molecules/ErrorCard/ErrorCard";
import * as Sentry from "@sentry/nextjs";
import { Component, type ReactNode } from "react";
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  onReset?: () => void;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack
        }
      },
      tags: {
        errorBoundary: "true",
        context: this.props.context || "application"
      }
    });
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };
  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8" data-sentry-component="ErrorBoundary" data-sentry-source-file="ErrorBoundary.tsx">
          <div className="relative w-full max-w-xl">
            <ErrorCard responseError={{
            message: this.state.error.message || "An unexpected error occurred. Our team has been notified and is working to resolve the issue."
          }} context={this.props.context || "application"} onRetry={this.handleReset} data-sentry-element="ErrorCard" data-sentry-source-file="ErrorBoundary.tsx" />
          </div>
        </div>;
    }
    return this.props.children;
  }
}